// src/components/camera/BreathingGraphSVG.js
import React, { useId } from "react";

/**
 * BreathingGraphSVG
 * @param {object} props
 * @param {number} [props.percent=0]  0..100 – portion filled with primary from left→right
 * @param {string} [props.primary="var(--color-primary)"]       CSS color for left side
 * @param {string} [props.grey="var(--color-tertiary)"]      CSS color for right side
 */
export const BreathingGraphSVG = ({
  percent = 0,
  primary = "#852170", // default to your primary
  grey = "#E8E8E9", // default to your tertiary
  outlineGrey = "#E8E8E9",
  outlineOpacity = 0.25,
  ...props
}) => {
  const id = useId();
  const p = Math.max(0, Math.min(100, percent)); // clamp
  const gradId = `breath-grad-${id}`;
  const maskId = `breath-mask-${id}`;
  const waveId = `wave-${id}`;

  return (
    <svg viewBox="1 12 132 33" aria-hidden focusable="false" {...props}>
      <defs>
        {/* One gradient spanning the whole viewBox width */}
        <linearGradient
          id={gradId}
          x1="0"
          y1="0"
          x2="133.5"
          y2="0"
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0%" stopColor={primary} />
          <stop offset={`${p}%`} stopColor={primary} />
          <stop offset={`${p}%`} stopColor={grey} />
          <stop offset="100%" stopColor={grey} />
        </linearGradient>

        {/* Mask built from the full waveform geometry */}
        <mask
          id={maskId}
          maskUnits="userSpaceOnUse"
          x="0"
          y="0"
          width="133.5"
          height="63.75"
        >
          {/* Use the same geometry, filled solid white so it becomes the visible area */}
          <use href={`#${waveId}`} fill="#fff" />
        </mask>
        <clipPath id="0c5e25eee7">
          <path d="M38.363 13H49.09v31.32H38.363Zm0 0"></path>
        </clipPath>
        <clipPath id="91d6f951dd">
          <path d="M39.637 42.664c1.031.762 1.547.11 1.914-.766 1.472-3.277 3.02-20.691 3.828-24.675.297-1.313.297-1.86.738-2.567.442-.656 1.324-1.64 1.84-1.64.371-.051 1.105.437 1.105.71 0 .383-1.18.766-1.546 1.368-.516.652-.442 1.308-.735 2.672-.812 4.043-1.695 20.746-3.758 24.406-.738 1.2-1.62 2.074-2.43 2.129-.738.11-2.21-.766-2.21-1.094 0-.219 1.254-.543 1.254-.543"></path>
        </clipPath>
        <clipPath id="f91833d8ee">
          <path d="M.363 0H11.09v31.32H.363Zm0 0"></path>
        </clipPath>
        <clipPath id="def12a2352">
          <path d="M1.637 29.664c1.031.762 1.547.11 1.914-.766C5.023 25.621 6.57 8.207 7.379 4.223c.297-1.313.297-1.86.738-2.567C8.56 1 9.441.016 9.957.016c.371-.051 1.105.437 1.105.71 0 .383-1.18.766-1.546 1.368-.516.652-.442 1.308-.735 2.672-.812 4.043-1.695 20.746-3.758 24.406-.738 1.2-1.62 2.074-2.43 2.129-.738.11-2.21-.766-2.21-1.094 0-.219 1.254-.543 1.254-.543"></path>
        </clipPath>
        <clipPath id="ebc3214964">
          <path d="M0 0h12v32H0z"></path>
        </clipPath>
        <clipPath id="7da5266499">
          <path d="M48.66 12.887h17.254v30.73H48.66Zm0 0"></path>
        </clipPath>
        <clipPath id="b196c7c6cf">
          <path d="M65.871 40.313c-3.332 2.855-3.836 2.96-4.64 3.066-1.313.156-3.633.371-4.997 0-1.109-.316-2.015-.738-2.773-1.691-1.059-1.32-1.512-3.489-2.117-6.286-1.16-5.125-3.13-22.242-2.473-22.457.203-.05.758 1.164 1.059 2.008.453 1.324.656 2.906.957 5.074.508 3.594.91 11.36 1.465 14.739.25 1.746.402 2.75.808 3.91.3.953.602 1.957 1.207 2.535.606.637 1.465.953 2.422 1.164 1.11.266 2.773.105 3.836 0 .805-.105 1.41-.105 2.066-.477.86-.476 1.97-2.48 2.524-2.43.3 0 .656.844.656.844"></path>
        </clipPath>
        <clipPath id="3000a70f92">
          <path d="M.66.887h17.254v30.73H.66Zm0 0"></path>
        </clipPath>
        <clipPath id="bf2c1af3bc">
          <path d="M17.871 28.313c-3.332 2.855-3.836 2.96-4.64 3.066-1.313.156-3.633.371-4.997 0-1.109-.317-2.015-.738-2.773-1.692-1.059-1.32-1.512-3.488-2.117-6.285C2.184 18.277.214 1.16.87.945c.203-.05.758 1.164 1.059 2.008.453 1.324.656 2.906.957 5.074.508 3.594.91 11.36 1.465 14.739.25 1.746.402 2.75.808 3.91.3.953.602 1.957 1.207 2.535.606.637 1.465.953 2.422 1.164 1.11.266 2.774.105 3.836 0 .805-.105 1.41-.105 2.066-.477.86-.476 1.97-2.48 2.524-2.43.3 0 .656.845.656.845"></path>
        </clipPath>
        <clipPath id="386f9f26f9">
          <path d="M0 0h18v32H0z"></path>
        </clipPath>
        <clipPath id="4bf4bb3912">
          <path d="M48.035 13.54h16.016v30.065H48.035Zm0 0"></path>
        </clipPath>
        <clipPath id="76190af411">
          <path d="M48.969 13.61c1.906 3.859 2.152 12.742 3.136 17.394.739 3.648 1.54 8.09 2.708 9.676.492.636.859.847 1.597 1.058.985.317 2.895.422 4.063.106 1.047-.266 1.968-1.586 2.644-1.64.371-.052.801.054.863.265.122.316-.37 1.11-.863 1.535-.676.578-1.844 1.11-3.015 1.32-1.352.266-3.567.371-4.797-.16-1.047-.422-1.785-1.48-2.215-2.219-.371-.476-.738-1.164-.555-1.48.125-.266.8-.477 1.11-.371.613.21.859 2.222 1.597 2.8.492.426 1.168.532 1.848.637.984.106 2.336-.054 3.32-.422 1.047-.37 2.215-1.957 2.832-1.957.367 0 .738.211.797.477 0 .316-1.043 1.16-1.723 1.586-.55.367-1.043.582-1.843.687-1.168.211-3.446.211-4.614-.16-.925-.316-1.476-.582-2.156-1.426-1.351-1.8-2.09-6.558-2.89-10.363-.985-4.707-1.231-13.695-2.09-15.703-.184-.531-.739-.637-.676-.898.058-.32.922-.743.922-.743"></path>
        </clipPath>
        <clipPath id="bd57863839">
          <path d="M.035.54h16.016v30.065H.035Zm0 0"></path>
        </clipPath>
        <clipPath id="d4bad55430">
          <path d="M.969.61C2.875 4.468 3.12 13.351 4.105 18.003c.739 3.648 1.54 8.09 2.707 9.676.493.636.86.847 1.598 1.058.985.317 2.895.422 4.063.106 1.047-.266 1.968-1.586 2.644-1.64.371-.052.801.054.863.265.122.316-.37 1.11-.863 1.535-.676.578-1.844 1.11-3.015 1.32-1.352.266-3.567.371-4.797-.16-1.047-.422-1.785-1.48-2.215-2.219-.371-.476-.738-1.164-.555-1.48.125-.266.8-.477 1.11-.371.613.21.859 2.222 1.597 2.8.492.426 1.168.532 1.848.637.984.106 2.336-.054 3.32-.422 1.047-.37 2.215-1.957 2.832-1.957.367 0 .738.211.797.477 0 .316-1.043 1.16-1.723 1.586-.55.367-1.043.582-1.843.687-1.168.211-3.446.211-4.614-.16-.925-.316-1.476-.582-2.156-1.426-1.351-1.8-2.09-6.558-2.89-10.363C1.828 13.246 1.582 4.258.723 2.25c-.184-.531-.739-.637-.676-.898.058-.32.922-.743.922-.743"></path>
        </clipPath>
        <clipPath id="259ba79db1">
          <path d="M0 0h17v31H0z"></path>
        </clipPath>
        <clipPath id="2ed4e77b99">
          <path d="M36.055 12.895h14.05v31.55h-14.05Zm0 0"></path>
        </clipPath>
        <clipPath id="5c60010613">
          <path d="M37.332 36.637c2.586 6.476 2.707 6.691 2.832 6.691.121 0 .246-.215.367-.484.371-.75.864-2.621 1.293-4.762.922-4.336 1.848-15.848 2.89-20.074.493-2.035.618-3.64 1.54-4.39.617-.536 1.785-.856 2.46-.641.68.265 1.477 1.87 1.356 2.406-.125.32-.738.75-.984.644-.43-.054-.43-1.93-.863-2.09-.305-.105-.922.27-1.168.536-.246.214-.246.484-.371 1.07-.739 2.68-2.461 17.453-3.63 22.484-.554 2.57-.859 4.926-1.66 5.782-.37.43-.863.59-1.292.59s-.801-.215-1.23-.59c-.923-1.016-3.2-5.942-2.708-6.797.184-.325 1.168-.375 1.168-.375"></path>
        </clipPath>
        <clipPath id="09a148cf35">
          <path d="M.055.895h14.05v31.55H.055Zm0 0"></path>
        </clipPath>
        <clipPath id="389fbf3285">
          <path d="M1.332 24.637c2.586 6.476 2.707 6.691 2.832 6.691.121 0 .246-.215.367-.484.371-.75.864-2.621 1.293-4.762.922-4.336 1.848-15.848 2.89-20.074.493-2.035.618-3.64 1.54-4.39.617-.536 1.785-.856 2.46-.641.68.265 1.477 1.87 1.356 2.406-.125.32-.738.75-.984.644-.43-.054-.43-1.93-.863-2.09-.305-.105-.922.27-1.168.536-.246.215-.246.484-.371 1.07-.739 2.68-2.461 17.453-3.63 22.484-.554 2.57-.859 4.926-1.66 5.782-.37.43-.863.59-1.292.59s-.801-.215-1.23-.59c-.923-1.016-3.2-5.942-2.708-6.797.184-.325 1.168-.375 1.168-.375"></path>
        </clipPath>
        <clipPath id="e9076a4022">
          <path d="M0 0h15v33H0z"></path>
        </clipPath>
        <clipPath id="3ff8a3386c">
          <path d="M1.254 31.742h36.785V40H1.254Zm0 0"></path>
        </clipPath>
        <clipPath id="2f232975f9">
          <path d="M1.36 38.398c1.808-1.195 2.042-4.035 2.906-4.93.55-.597 1.492-.972 2.199-.898.707.078 1.336.75 1.883 1.496.867.973 1.57 3.883 2.277 4.258a.63.63 0 0 0 .785.074c.63-.449.55-3.66 1.336-4.632a2.91 2.91 0 0 1 2.043-1.196c.707-.074 1.727.153 2.356.676.785.746.785 2.988 1.335 3.734.391.524.786.97 1.176.895.63-.148.785-3.512 1.809-4.406.785-.746 2.2-1.344 3.219-.973 1.492.45 2.984 5.68 4.16 5.606.945-.075 1.414-4.184 2.36-5.23.624-.673 1.41-1.122 2.116-1.122.707.074 1.492.598 2.121 1.27 1.024 1.195 2.985 5.156 2.516 5.828-.16.297-1.023.297-1.574 0-.942-.672-1.727-5.305-2.59-5.602-.473-.152-1.02.223-1.414.672-.707.746-.785 2.914-1.492 3.883-.469.746-1.098 1.57-1.727 1.719-.707.148-1.648-.223-2.355-.82-1.024-.9-1.809-4.41-2.672-4.782-.469-.152-1.098.148-1.492.523-.63.598-.47 2.989-1.176 3.883-.473.598-1.336 1.196-2.043 1.196-.707-.075-1.414-.672-2.043-1.344-.863-.899-1.414-3.887-2.117-4.11-.395-.148-.946.223-1.336.672-.551.746-.395 2.989-.864 3.809-.394.598-.707 1.121-1.257 1.273-.63.149-1.727 0-2.356-.523-1.023-.672-1.414-3.363-2.199-4.184-.395-.523-.941-1.047-1.336-.972-.785.222-1.254 4.183-2.2 5.082-.706.597-1.96.968-2.355.672-.234-.153 0-1.497 0-1.497"></path>
        </clipPath>
        <clipPath id="8fd79efafe">
          <path d="M.254.742h36.785V9H.254Zm0 0"></path>
        </clipPath>
        <clipPath id="5abd980f6b">
          <path d="M.36 7.398c1.808-1.195 2.042-4.035 2.906-4.93.55-.597 1.492-.972 2.199-.898.707.078 1.336.75 1.883 1.496.867.973 1.57 3.883 2.277 4.258a.63.63 0 0 0 .785.074c.63-.449.55-3.66 1.336-4.632A2.91 2.91 0 0 1 13.79 1.57c.707-.074 1.727.153 2.356.676.785.746.785 2.988 1.335 3.734.391.524.786.97 1.176.895.63-.148.785-3.512 1.809-4.406.785-.746 2.2-1.344 3.219-.973 1.492.45 2.984 5.68 4.16 5.606.945-.075 1.414-4.184 2.36-5.23.624-.673 1.41-1.122 2.116-1.122.707.074 1.492.598 2.121 1.27 1.024 1.195 2.985 5.156 2.516 5.828-.16.297-1.023.297-1.574 0-.942-.672-1.727-5.305-2.59-5.602-.473-.152-1.02.223-1.414.672-.707.746-.785 2.914-1.492 3.883-.469.746-1.098 1.57-1.727 1.719-.707.148-1.648-.223-2.355-.82-1.024-.9-1.809-4.41-2.672-4.782-.469-.152-1.098.148-1.492.523-.63.598-.47 2.989-1.176 3.883-.473.598-1.336 1.196-2.043 1.196-.707-.075-1.414-.672-2.043-1.344-.863-.899-1.414-3.887-2.117-4.11-.395-.148-.946.223-1.336.672-.551.746-.395 2.989-.864 3.809-.394.598-.707 1.121-1.257 1.273-.63.149-1.727 0-2.356-.523-1.023-.672-1.414-3.363-2.199-4.184-.395-.523-.941-1.047-1.336-.972-.785.222-1.254 4.183-2.2 5.082-.706.597-1.96.968-2.355.672-.234-.153 0-1.497 0-1.497"></path>
        </clipPath>
        <clipPath id="e156cd25a1">
          <path d="M0 0h38v9H0z"></path>
        </clipPath>
        <clipPath id="d3c9f71e36">
          <path d="M63.434 39.445c1.851-1.195 2.09-4.035 2.976-4.93.563-.597 1.528-.972 2.254-.898.723.074 1.367.746 1.93 1.496.883.969 1.61 3.883 2.332 4.258a.66.66 0 0 0 .804.074c.645-.449.563-3.66 1.368-4.632a2.99 2.99 0 0 1 2.093-1.196c.723-.074 1.77.149 2.41.672.805.75.805 2.988 1.368 3.738.402.52.804.97 1.207.895.644-.149.804-3.512 1.851-4.406.805-.75 2.254-1.348 3.297-.973 1.531.45 3.059 5.68 4.266 5.602.965-.075 1.445-4.184 2.414-5.227.64-.672 1.445-1.121 2.172-1.121.722.074 1.527.598 2.172 1.27 1.043 1.195 3.054 5.156 2.574 5.828-.164.296-1.047.296-1.61 0-.964-.672-1.769-5.305-2.656-5.606-.48-.148-1.047.227-1.449.672-.723.75-.8 2.914-1.527 3.887-.48.746-1.125 1.57-1.77 1.718-.722.149-1.687-.226-2.414-.824-1.043-.894-1.848-4.406-2.734-4.781-.48-.148-1.125.152-1.528.523-.644.598-.484 2.989-1.207 3.887-.484.598-1.367 1.195-2.09 1.195-.726-.074-1.449-.671-2.093-1.343-.883-.899-1.45-3.887-2.172-4.11-.402-.152-.965.223-1.367.672-.563.746-.403 2.988-.887 3.809-.402.597-.723 1.12-1.285 1.27-.645.152-1.77 0-2.414-.524-1.047-.672-1.45-3.36-2.25-4.184-.403-.52-.969-1.043-1.371-.968-.801.222-1.285 4.183-2.25 5.078-.723.597-2.012.972-2.414.672-.243-.149 0-1.493 0-1.493"></path>
        </clipPath>
        <clipPath id="8d3cf2efdb">
          <path d="M.305.785H38V9.06H.305Zm0 0"></path>
        </clipPath>
        <clipPath id="8b5f3ae3a8">
          <path d="M.434 7.445C2.285 6.25 2.524 3.41 3.41 2.515c.563-.597 1.527-.972 2.254-.898.723.074 1.367.746 1.93 1.496.883.969 1.61 3.883 2.332 4.258a.66.66 0 0 0 .804.074c.645-.449.563-3.66 1.368-4.632a2.99 2.99 0 0 1 2.093-1.196c.723-.074 1.77.149 2.41.672.805.75.805 2.988 1.368 3.738.402.52.804.97 1.207.895.644-.149.804-3.512 1.851-4.406.805-.75 2.254-1.348 3.297-.973 1.531.45 3.059 5.68 4.266 5.602.965-.075 1.445-4.184 2.414-5.227.64-.672 1.445-1.121 2.172-1.121.722.074 1.527.598 2.172 1.27 1.043 1.195 3.054 5.156 2.574 5.828-.164.296-1.047.296-1.61 0-.964-.672-1.769-5.305-2.656-5.606-.48-.148-1.047.227-1.449.672-.723.75-.8 2.914-1.527 3.887-.48.746-1.125 1.57-1.77 1.718-.723.149-1.687-.226-2.414-.824-1.043-.894-1.848-4.406-2.734-4.781-.48-.148-1.125.152-1.528.523-.644.598-.484 2.989-1.207 3.887-.484.598-1.367 1.195-2.09 1.195-.726-.074-1.449-.671-2.093-1.343-.883-.899-1.45-3.887-2.172-4.11-.402-.152-.965.223-1.367.672-.563.746-.403 2.988-.887 3.809-.402.597-.723 1.12-1.285 1.27-.645.152-1.77 0-2.414-.524-1.047-.672-1.45-3.36-2.25-4.184-.403-.52-.969-1.043-1.371-.968-.801.222-1.285 4.183-2.25 5.078-.723.597-2.012.972-2.414.672-.243-.149 0-1.493 0-1.493"></path>
        </clipPath>
        <clipPath id="35f1d5e642">
          <path d="M0 0h38v10H0z"></path>
        </clipPath>
        <clipPath id="57e43c73f4">
          <path d="M100.344 32.21h32.644v7.927h-32.644Zm0 0"></path>
        </clipPath>
        <clipPath id="b5a0587ee7">
          <path d="M100.496 38.59c1.852-1.145 2.094-3.863 2.977-4.723.566-.574 1.53-.93 2.254-.86.722.071 1.367.716 1.93 1.43.886.934 1.609 3.723 2.335 4.079a.675.675 0 0 0 .801.074c.644-.43.566-3.508 1.371-4.438.48-.644 1.285-1.074 2.09-1.144.723-.07 1.77.144 2.414.644.805.715.805 2.864 1.367 3.578.403.5.805.93 1.207.86.645-.145.805-3.363 1.848-4.223.805-.715 2.254-1.289 3.3-.93 1.528.43 3.055 5.438 4.262 5.368.965-.075 1.45-4.008 2.414-5.012.645-.64 1.45-1.07 2.172-1.07.723.07 1.528.57 2.172 1.215 1.047 1.144 3.059 4.937 2.574 5.582-.16.285-1.047.285-1.609 0-.965-.645-1.77-5.083-2.652-5.368-.485-.144-1.047.215-1.45.645-.722.715-.804 2.789-1.527 3.719-.484.714-1.129 1.504-1.77 1.648-.726.14-1.69-.215-2.413-.789-1.047-.86-1.852-4.223-2.735-4.578-.484-.145-1.129.14-1.531.5-.64.574-.48 2.863-1.207 3.719-.48.574-1.367 1.148-2.09 1.148-.723-.074-1.45-.644-2.09-1.289-.887-.86-1.45-3.723-2.172-3.937-.402-.141-.968.214-1.37.644-.563.715-.403 2.863-.884 3.648-.402.575-.722 1.075-1.289 1.22-.64.14-1.77 0-2.41-.5-1.047-.645-1.45-3.223-2.254-4.009-.402-.5-.965-1.004-1.367-.93-.805.216-1.289 4.005-2.254 4.864-.722.574-2.012.93-2.414.645-.238-.145 0-1.43 0-1.43"></path>
        </clipPath>
        <clipPath id="c439553cf4">
          <path d="M.344.21h32.644v7.927H.344Zm0 0"></path>
        </clipPath>
        <clipPath id="e27e4eea87">
          <path d="M.496 6.59C2.348 5.445 2.59 2.727 3.473 1.867c.566-.574 1.53-.93 2.254-.86.722.071 1.367.716 1.93 1.43.886.934 1.609 3.723 2.335 4.079a.675.675 0 0 0 .801.074c.645-.43.566-3.508 1.371-4.438.48-.644 1.285-1.074 2.09-1.144.723-.07 1.77.144 2.414.644.805.715.805 2.864 1.367 3.578.402.5.805.93 1.207.86.645-.145.805-3.363 1.848-4.223.805-.715 2.254-1.289 3.3-.93 1.528.43 3.055 5.438 4.262 5.368.965-.075 1.45-4.008 2.414-5.012.645-.64 1.45-1.07 2.172-1.07.723.07 1.528.57 2.172 1.215 1.047 1.144 3.059 4.937 2.574 5.582-.16.285-1.047.285-1.609 0-.965-.645-1.77-5.082-2.652-5.368-.485-.144-1.047.215-1.45.645-.722.715-.804 2.789-1.527 3.719-.484.714-1.129 1.504-1.77 1.648-.726.14-1.69-.215-2.413-.789-1.047-.86-1.852-4.223-2.735-4.578-.484-.145-1.129.14-1.531.5-.64.574-.48 2.863-1.207 3.719-.48.574-1.367 1.148-2.09 1.148-.723-.074-1.45-.644-2.09-1.289-.887-.86-1.45-3.723-2.172-3.937-.402-.141-.968.214-1.37.644-.563.715-.403 2.863-.884 3.648-.402.575-.722 1.075-1.289 1.22-.64.14-1.77 0-2.41-.5-1.047-.645-1.45-3.223-2.254-4.009-.402-.5-.965-1.003-1.367-.93-.805.216-1.289 4.005-2.254 4.864-.723.574-2.012.93-2.414.645-.238-.145 0-1.43 0-1.43"></path>
        </clipPath>
        <clipPath id="c48ef523fe">
          <path d="M0 0h33v9H0z"></path>
        </clipPath>
      </defs>
      <g id={waveId} fill={outlineGrey}>
        <g clipPath="url(#0c5e25eee7)">
          <g clipPath="url(#91d6f951dd)">
            <g clipPath="url(#ebc3214964)" transform="translate(38 13)">
              <g clipPath="url(#f91833d8ee)">
                <g clipPath="url(#def12a2352)">
                  <path d="M0 0h12v32H0z"></path>
                </g>
              </g>
            </g>
          </g>
        </g>
        <g clipPath="url(#7da5266499)">
          <g clipPath="url(#b196c7c6cf)">
            <g clipPath="url(#386f9f26f9)" transform="translate(48 12)">
              <g clipPath="url(#3000a70f92)">
                <g clipPath="url(#bf2c1af3bc)">
                  <path d="M0 0h18v32H0z"></path>
                </g>
              </g>
            </g>
          </g>
        </g>
        <g clipPath="url(#4bf4bb3912)">
          <g clipPath="url(#76190af411)">
            <g clipPath="url(#259ba79db1)" transform="translate(48 13)">
              <g clipPath="url(#bd57863839)">
                <g clipPath="url(#d4bad55430)">
                  <path d="M0 0h17v31H0z"></path>
                </g>
              </g>
            </g>
          </g>
        </g>
        <g clipPath="url(#2ed4e77b99)">
          <g clipPath="url(#5c60010613)">
            <g clipPath="url(#e9076a4022)" transform="translate(36 12)">
              <g clipPath="url(#09a148cf35)">
                <g clipPath="url(#389fbf3285)">
                  <path d="M0 0h15v33H0z"></path>
                </g>
              </g>
            </g>
          </g>
        </g>
        <g clipPath="url(#3ff8a3386c)">
          <g clipPath="url(#2f232975f9)">
            <g clipPath="url(#e156cd25a1)" transform="translate(1 31)">
              <g clipPath="url(#8fd79efafe)">
                <g clipPath="url(#5abd980f6b)">
                  <path d="M0 0h38v9H0z"></path>
                </g>
              </g>
            </g>
          </g>
        </g>
        <g clipPath="url(#0168242277)">
          <g clipPath="url(#d3c9f71e36)">
            <g clipPath="url(#35f1d5e642)" transform="translate(63 32)">
              <g clipPath="url(#8d3cf2efdb)">
                <g clipPath="url(#8b5f3ae3a8)">
                  <path d="M0 0h38v10H0z"></path>
                </g>
              </g>
            </g>
          </g>
        </g>
        <g clipPath="url(#57e43c73f4)">
          <g clipPath="url(#b5a0587ee7)">
            <g clipPath="url(#c48ef523fe)" transform="translate(100 32)">
              <g clipPath="url(#c439553cf4)">
                <g clipPath="url(#e27e4eea87)">
                  <path d="M0 0h33v9H0z"></path>
                </g>
              </g>
            </g>
          </g>
        </g>
      </g>
      <rect
        x="0"
        y="0"
        width="133.5"
        height="63.75"
        fill={`url(#${gradId})`}
        mask={`url(#${maskId})`}
      />
    </svg>
  );
};
